import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { CheckCircle, XCircle, CircleNotch } from "@phosphor-icons/react";
import { LogoMark, Wordmark } from "@/components/Logo";

// Landed on after a Stripe Pricing Table purchase. Stripe's "after payment" redirect is
// configured (in the Stripe Dashboard) to send customers here with ?session_id={CHECKOUT_SESSION_ID}.
// There's no free trial and no self-serve sign-up — this page, backed by POST /billing/confirm,
// is the only way a paying customer's account gets provisioned and a login link gets sent.
export default function ThankYou() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("loading"); // loading | sent | already_active | error
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [resending, setResending] = useState(false);

  const confirm = () => {
    if (!sessionId) { setStatus("error"); setError("We couldn't find your payment details in this link."); return; }
    setStatus("loading");
    api.confirmCheckout(sessionId)
      .then((r) => {
        setEmail(r.data.email);
        setStatus(r.data.already_active ? "already_active" : "sent");
      })
      .catch((e) => { setStatus("error"); setError(apiError(e, "Something went wrong confirming your payment")); });
  };

  useEffect(() => { confirm(); }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const resend = () => {
    setResending(true);
    api.confirmCheckout(sessionId)
      .then(() => {})
      .catch(() => {})
      .finally(() => setResending(false));
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--bg)" }}>
      <div className="w-full max-w-sm text-center fade-up">
        <div className="flex items-center justify-center gap-3 mb-8">
          <LogoMark size={36} />
          <Wordmark className="text-lg text-white" />
        </div>

        {status === "loading" && (
          <>
            <CircleNotch size={32} className="animate-spin mx-auto mb-3" color="var(--accent)" />
            <p className="text-sm" style={{ color: "var(--text-2)" }}>Confirming your payment...</p>
          </>
        )}

        {status === "sent" && (
          <>
            <CheckCircle size={40} weight="fill" color="var(--success)" className="mx-auto mb-3" />
            <h2 className="text-xl font-bold mb-2">Payment received — check your email</h2>
            <p className="text-sm mb-6" style={{ color: "var(--text-2)" }}>
              We sent a link to <span style={{ color: "var(--text)" }}>{email}</span> so you can set a
              password and log in to JumpSEO. It can take a couple of minutes to arrive.
            </p>
            <button onClick={resend} disabled={resending} className="text-sm font-semibold hover:underline disabled:opacity-60" style={{ color: "var(--accent)" }}>
              {resending ? "Resending..." : "Didn't get it? Resend"}
            </button>
          </>
        )}

        {status === "already_active" && (
          <>
            <CheckCircle size={40} weight="fill" color="var(--success)" className="mx-auto mb-3" />
            <h2 className="text-xl font-bold mb-2">You're all set</h2>
            <p className="text-sm mb-6" style={{ color: "var(--text-2)" }}>
              Your account is active on the {" "}<span style={{ color: "var(--text)" }}>{email}</span>{" "} email.
              Sign in with the password you already have.
            </p>
            <Link to="/login" className="inline-block px-6 py-2.5 text-sm font-bold text-white hover:opacity-90 transition-opacity" style={{ background: "var(--primary)" }}>
              Sign in
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle size={40} weight="fill" color="var(--danger)" className="mx-auto mb-3" />
            <h2 className="text-xl font-bold mb-2">We couldn't confirm that</h2>
            <p className="text-sm mb-6" style={{ color: "var(--text-2)" }}>{error}</p>
            <p className="text-sm" style={{ color: "var(--text-2)" }}>
              If you were just charged, contact{" "}
              <a href="mailto:support@jumpseo.ai" className="font-semibold hover:underline" style={{ color: "var(--accent)" }}>support@jumpseo.ai</a>{" "}
              and we'll get you access.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
